import { cx } from 'class-variance-authority';
import capitalize from 'lodash/capitalize';
import isUndefined from 'lodash/isUndefined';
import { forwardRef } from 'react';

import { useTranslation } from 'react-i18next';

import type { FormattedAddress } from '../../../../../../onboarding-business-insight.types';
import SubmittedVerified from './components/submitted-verified';

type AddressCardProps = {
  address: FormattedAddress;
  isSelected: boolean;
  onSelect: (address: FormattedAddress) => void;
};

const AddressCard = forwardRef<HTMLButtonElement, AddressCardProps>(({ address, isSelected, onSelect }, ref) => {
  const { t } = useTranslation('entity-details', { keyPrefix: 'onboardings' });
  const { id, addressLine1, addressLine2, city, state, postalCode, propertyType, submitted, deliverable, verified } =
    address;
  const showNotes = !propertyType || !isUndefined(deliverable);

  const handleSelect = () => {
    const card = document.getElementById(`offices-card-${id}`);
    const parent = card?.parentElement;
    if (card && parent) {
      parent.scrollTo({
        top: card.offsetTop,
        behavior: 'smooth',
      });
    }
    onSelect(address);
  };

  const formatAddressLine = (addressFields: (string | undefined)[]) =>
    addressFields.filter(field => Boolean(field)).join(', ');

  const getUspsText = (text: string) => (
    <p className="text-body-2 text-tertiary">
      {t($ => $.offices.usps)} &middot; {text}
    </p>
  );

  return (
    <button
      id={`offices-card-${id}`}
      ref={ref}
      className={cx(
        'w-full relative flex flex-col border border-solid rounded bg-primary cursor-pointer transition-colors duration-100 ease-in-out box-border',
        isSelected ? 'border-secondary' : 'border-tertiary hover:border-primary hover:bg-hover',
      )}
      onClick={handleSelect}
      type="button"
    >
      <div className="flex flex-col w-full gap-2 p-3 text-left">
        <SubmittedVerified submitted={submitted} verified={verified} />
        <div className="flex flex-col">
          <p className={cx('text-label-2', isSelected ? 'text-primary' : 'text-tertiary')}>
            {formatAddressLine([addressLine1, addressLine2])}
          </p>
          <p className={cx('text-body-2', !isSelected && 'text-tertiary')}>
            {formatAddressLine([city, state, postalCode])}
          </p>
        </div>
        {showNotes && (
          <div className="flex flex-col">
            <p className="text-label-2 text-tertiary">{t($ => $.offices.notes)}</p>
            {!isUndefined(propertyType) && getUspsText(capitalize(propertyType))}
            {!isUndefined(deliverable) &&
              getUspsText(deliverable ? t($ => $.offices.deliverable) : t($ => $.offices.notDeliverable))}
          </div>
        )}
      </div>
    </button>
  );
});

export default AddressCard;
